/**
 * My Notes — small list window for saved notes.
 *
 * On mount: GET /api/notes → render a table of {title, updated_at}.
 * Actions:
 *   - Double-click a row → open a Notepad with that note pre-loaded
 *     (fires `lottel:dynamic` with a full appSettings-shaped payload).
 *   - Open button       → same as double-click on the selected row.
 *   - Delete button     → DELETE /api/notes/:id then refresh.
 *   - New Note button   → open a blank Notepad ready to save.
 *   - Refresh button    → re-fetch the list.
 *
 * The window also listens for `lottel:notes-changed` so that when a Notepad
 * saves elsewhere, this list stays fresh.
 */

import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

import { useAuth } from 'auth/AuthContext';
import { api, ApiError } from 'api/client';
import Notepad from '../Notepad';

import notepadIcon from 'assets/windowsIcons/327(16x16).png';
import notepadIconLarge from 'assets/windowsIcons/327(32x32).png';

/**
 * Compose an appSettings-shaped payload that opens a Notepad with the given
 * note pre-loaded. Used both by double-click and by the "New Note" button
 * (which passes noteId=null to get a blank editable canvas).
 */
function notepadPayload({ noteId, title }) {
  return {
    header: {
      icon: notepadIcon,
      title: title
        ? `${title} - Notepad`
        : noteId
        ? 'Note - Notepad'
        : 'Untitled - Notepad',
    },
    component: Notepad,
    defaultSize: { width: 500, height: 440 },
    defaultOffset: {
      x: Math.max(40, Math.round(window.innerWidth / 2 - 240)),
      y: Math.max(40, Math.round(window.innerHeight / 2 - 220)),
    },
    resizable: true,
    minimized: false,
    maximized: window.innerWidth < 540,
    multiInstance: true,
    injectProps: { noteId: noteId || undefined, title: title || undefined },
  };
}

function launchNotepad({ noteId, title }) {
  window.dispatchEvent(
    new CustomEvent('lottel:dynamic', {
      detail: notepadPayload({ noteId, title }),
    }),
  );
}

export default function MyNotes({ onClose }) {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get('/api/notes');
      setNotes(Array.isArray(data && data.notes) ? data.notes : []);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    load();
    function onChanged() {
      load();
    }
    window.addEventListener('lottel:notes-changed', onChanged);
    return () => window.removeEventListener('lottel:notes-changed', onChanged);
  }, [user, load]);

  function onOpen(note) {
    launchNotepad({ noteId: note.id, title: note.title });
  }

  function onNewNote() {
    launchNotepad({ noteId: null, title: '' });
  }

  async function onDelete(note) {
    if (busy) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete "${note.title}"? This can't be undone.`)) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.del('/api/notes/' + note.id);
      setNotes(prev => prev.filter(n => n.id !== note.id));
      if (selectedId === note.id) setSelectedId(null);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const selected = notes.find(n => n.id === selectedId) || null;

  if (!user) {
    return (
      <Div>
        <div className="mn__empty">
          <img src={notepadIconLarge} alt="" />
          <p>Sign in to view your saved notes.</p>
          <button type="button" onClick={onClose}>
            Close
          </button>
        </div>
      </Div>
    );
  }

  return (
    <Div>
      <div className="mn__toolbar">
        <button type="button" onClick={onNewNote}>
          New Note
        </button>
        <button
          type="button"
          onClick={() => selected && onOpen(selected)}
          disabled={!selected}
        >
          Open
        </button>
        <button
          type="button"
          onClick={() => selected && onDelete(selected)}
          disabled={!selected || busy}
        >
          Delete
        </button>
        <div className="mn__toolbar__spacer" />
        <button type="button" onClick={load} disabled={loading}>
          Refresh
        </button>
      </div>

      {error && <div className="mn__error">{error}</div>}

      <div className="mn__list">
        {loading ? (
          <div className="mn__hint">Loading…</div>
        ) : notes.length === 0 ? (
          <div className="mn__hint">
            No saved notes yet. Open a Notepad and choose File → Save to My
            Notes.
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Title</th>
                <th>Last modified</th>
              </tr>
            </thead>
            <tbody>
              {notes.map(note => (
                <tr
                  key={note.id}
                  className={selectedId === note.id ? 'mn__row--selected' : ''}
                  onClick={() => setSelectedId(note.id)}
                  onDoubleClick={() => onOpen(note)}
                >
                  <td>
                    <img src={notepadIcon} alt="" className="mn__row__icon" />
                    {note.title || 'Untitled'}
                  </td>
                  <td>{formatTimestamp(note.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Div>
  );
}

function formatTimestamp(ts) {
  if (!ts) return '';
  const d = new Date(ts * 1000);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString()}`;
}

function errorMessage(err) {
  if (err instanceof ApiError) {
    const map = {
      unauthorized: 'Please sign in again.',
      csrf_invalid: 'Session expired — please reload the page.',
      not_found: 'That note no longer exists.',
      network_error: 'Cannot reach the server.',
    };
    return map[err.code] || err.message || 'Something went wrong.';
  }
  return 'Something went wrong.';
}

const Div = styled.div`
  background: #ece9d8;
  color: #000;
  font-family: Tahoma, sans-serif;
  font-size: 11px;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 8px;
  box-sizing: border-box;

  .mn__toolbar {
    display: flex;
    gap: 4px;
    align-items: center;
    margin-bottom: 6px;
  }
  .mn__toolbar__spacer {
    flex: 1;
  }
  .mn__toolbar button {
    min-width: 72px;
    padding: 3px 10px;
    font: inherit;
    background: linear-gradient(#fefefe 0%, #dedede 100%);
    border: 1px solid #003c74;
    border-radius: 3px;
    cursor: pointer;
  }
  .mn__toolbar button:active {
    background: linear-gradient(#dedede 0%, #fefefe 100%);
  }
  .mn__toolbar button[disabled] {
    color: #888;
    cursor: default;
  }

  .mn__error {
    background: #fdecec;
    border: 1px solid #d13438;
    color: #8b0000;
    padding: 4px 6px;
    margin-bottom: 6px;
  }
  .mn__hint {
    color: #555;
    padding: 8px;
  }

  .mn__list {
    background: #fff;
    border: 1px solid #a4b0be;
    flex: 1;
    overflow-y: auto;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 11px;
  }
  thead th {
    text-align: left;
    background: #ece9d8;
    padding: 3px 6px;
    border-bottom: 1px solid #a4b0be;
    position: sticky;
    top: 0;
    z-index: 1;
  }
  tbody td {
    padding: 3px 6px;
    border-bottom: 1px solid #eee;
    cursor: default;
  }
  tbody tr:hover {
    background: #f2f6ff;
  }
  tbody .mn__row--selected,
  tbody .mn__row--selected:hover {
    background: #245edc;
    color: #fff;
  }
  .mn__row__icon {
    width: 16px;
    height: 16px;
    vertical-align: middle;
    margin-right: 6px;
  }

  .mn__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    gap: 8px;
  }
  .mn__empty img {
    width: 32px;
    height: 32px;
  }
  .mn__empty button {
    padding: 3px 10px;
    background: linear-gradient(#fefefe 0%, #dedede 100%);
    border: 1px solid #003c74;
    border-radius: 3px;
    cursor: pointer;
    font: inherit;
  }
`;
